// src/pages/controleBar/mesas/FecharMesaModal.js
import React, { useState, useEffect, useRef } from "react";
import { useComanda } from "../../../contexts/controleBar/comandas/ComandaContext";
import { useAlert } from "../../../contexts/geral/AlertContext";
import { Nota } from "../../../models/vendas/Nota";
import { usePrinter } from '../../../contexts/geral/PrinterContext';
import ModalFinalizarVenda from "../../vendas/ModalFinalizarVenda";
import { formatNumeric, fromMoneyCents, parseNumeric, sumMoney, toMoneyCents } from "../../../utils/numberUtils";
import { useNavigate } from "react-router-dom";
import { StatusComanda } from "../../../models/controleBar/comandas/StatusComanda";
import ModalPortal from '../../../components/modalForm/ModalPortal';
import { montarNotaMesaPDV, ratearTotalMesaPDV } from '../../../services/vendas/PDVService';

const FecharMesaModal = ({ open, onClose, mesa, comandas, onPrint, onMesaFechada }) => {
  const navigate = useNavigate();
  const { imprimirComprovantesQuitados } = usePrinter();
  const comandasAntesPagamento = useRef([]);
  const { showAlert } = useAlert();  
  const { fetchComandas } = useComanda();

  const [valores, setValores] = useState({});
  const [total, setTotal] = useState("0,00");
  const [totalRestante, setTotalRestante] = useState("0,00");

  const [nota, setNota] = useState(new Nota());
  const [showFinalizarVenda, setShowFinalizarVenda] = useState(false);

  // 🔹 Inicializa valores
  useEffect(() => {
    if (!comandas?.length) return;

    let inicial = {};
    const valoresRestantes = [];

    comandas.forEach(c =>
      c.comandaItens?.forEach(i => {
        const restante = fromMoneyCents(Math.max(0, toMoneyCents(i.valorTotal) - toMoneyCents(i.valorPago)));
        valoresRestantes.push(restante);

        inicial[i.id] = formatNumeric(restante);
      })
    );

    const somaRestante = sumMoney(valoresRestantes);

    setValores(inicial);

    // Total bruto da mesa é exibido pela soma exata das colunas abaixo.

    // Total que ainda falta pagar (mesa inteira)
    setTotal(formatNumeric(somaRestante));

    // Total restante começa igual ao total a pagar
    setTotalRestante(formatNumeric(somaRestante));
  }, [comandas]);



  if (!open || !comandas?.length) return null;


  const handleFocusSelectAll = (e) => e.target.select();


  // 🔹 QUANDO ALTERA O VALOR INDIVIDUAL
  const handleChangeItem = (id, value) => {
    const valorStr = value.replace(/[^\d,]/g, "");

    const novoValores = { ...valores, [id]: valorStr };
    setValores(novoValores);

    const soma = sumMoney(Object.values(novoValores));

    setTotal(formatNumeric(soma));

    const saldoMesa = sumMoney(comandas.flatMap(c => c.comandaItens.map(i =>
      fromMoneyCents(Math.max(0, toMoneyCents(i.valorTotal) - toMoneyCents(i.valorPago)))
    )));
    const restante = fromMoneyCents(toMoneyCents(saldoMesa) - toMoneyCents(soma));
    setTotalRestante(formatNumeric(restante));
  };


  const handleBlurFormatItem = (id) => {
    const valorNum = parseNumeric(valores[id]);
    const item = comandas.flatMap(c => c.comandaItens).find(i => i.id === id);
    const saldoItem = item
      ? fromMoneyCents(Math.max(0, toMoneyCents(item.valorTotal) - toMoneyCents(item.valorPago)))
      : 0;
    if (toMoneyCents(valorNum) > toMoneyCents(saldoItem)) {
      showAlert('O valor a pagar não pode superar o saldo do item.', 'warning');
      handleChangeItem(id, formatNumeric(saldoItem));
      return;
    }
    setValores(prev => ({ ...prev, [id]: formatNumeric(valorNum) }));
  };


  // 🔹 QUANDO ALTERA O TOTAL GERAL
  const handleChangeTotal = (value) => {
    const valorStr = value.replace(/[^\d,]/g, "");
    setTotal(valorStr);

    const novoTotal = parseNumeric(valorStr);

    try {
      // Somente itens com saldo participam; os centavos restantes seguem o
      // método das maiores frações e nunca são lançados em item já quitado.
      const rateio = ratearTotalMesaPDV({ comandas, total: novoTotal });
      setValores(Object.fromEntries(
        Object.entries(rateio).map(([id, valor]) => [id, formatNumeric(valor)])
      ));

      const saldoMesa = sumMoney(comandas.flatMap(c => c.comandaItens.map(i =>
        fromMoneyCents(Math.max(0, toMoneyCents(i.valorTotal) - toMoneyCents(i.valorPago)))
      )));
      setTotalRestante(formatNumeric(fromMoneyCents(toMoneyCents(saldoMesa) - toMoneyCents(novoTotal))));
    } catch (_) {
      // Durante a digitação o texto pode ficar temporariamente acima do saldo.
      // A validação definitiva ocorre no blur e antes de abrir a finalização.
    }
  };



  const handleBlurFormatTotal = () => {
    const valorNum = parseNumeric(total);
    try {
      const rateio = ratearTotalMesaPDV({ comandas, total: valorNum });
      setValores(Object.fromEntries(
        Object.entries(rateio).map(([id, valor]) => [id, formatNumeric(valor)])
      ));
      setTotal(formatNumeric(valorNum));
    } catch (error) {
      showAlert(error.message, 'warning');
      const totalValido = sumMoney(Object.values(valores));
      setTotal(formatNumeric(totalValido));
      const saldoMesa = sumMoney(comandas.flatMap(c => c.comandaItens.map(i =>
        fromMoneyCents(Math.max(0, toMoneyCents(i.valorTotal) - toMoneyCents(i.valorPago)))
      )));
      setTotalRestante(formatNumeric(fromMoneyCents(toMoneyCents(saldoMesa) - toMoneyCents(totalValido))));
    }
  };


  // 🔹 CONFIRMAR — montar nota igual FecharComandaModal
  const handleConfirmar = () => {
    comandasAntesPagamento.current = (comandas || []).map(c => ({ id: c.id, status: c.status }));
    try {
      const notaPayload = montarNotaMesaPDV({
        clienteId: mesa.clienteId || 1,
        comandas,
        valores
      });
      setNota(notaPayload);
      setShowFinalizarVenda(true);
    } catch (error) {
      showAlert(error.message, 'warning');
    }
  };

  // 🔹 Cálculo das colunas da table
  const totalColunaTotal = comandas
    .reduce((acc, c) => sumMoney([acc, ...c.comandaItens.map(i => i.valorTotal)]), 0);

  const totalColunaPago = comandas
    .reduce((acc, c) => sumMoney([acc, ...c.comandaItens.map(i => i.valorPago)]), 0);

  const totalColunaValorAPagar = sumMoney(Object.values(valores));


  return (
    <ModalPortal show={open}>
    <div className="modal-overlay">
      <div className="modal-container">
        <h2>Fechar Mesa #{mesa?.codigo}</h2>

        <div className="modal-body">
          <table className="modal-tabela">
            <thead>
              <tr>
                <th>Comanda</th>
                <th>Item</th>
                <th>Descrição</th>
                <th>Total</th>
                <th>Pago</th>
                <th>Valor a Pagar</th>
              </tr>
            </thead>

            <tbody>
              {comandas.map(c =>
                c.comandaItens.map(i => (
                  <tr key={i.id}>
                    <td>#{c.codigo}</td>
                    <td>{i.item}</td>
                    <td>{i.produto?.descricao || i.xProd}</td>
                    <td style={{ textAlign: "right" }}>
                      {formatNumeric(i.valorTotal)}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {formatNumeric(i.valorPago)}
                    </td>

                    <td style={{ textAlign: "right", maxWidth: 100 }}>
                      <input
                        type="text"
                        value={valores[i.id] || "0,00"}
                        onChange={(e) => handleChangeItem(i.id, e.target.value)}
                        onFocus={handleFocusSelectAll}
                        onBlur={() => handleBlurFormatItem(i.id)}
                        inputMode="decimal"
                        style={{ width: "100%", textAlign: "right" }}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr className="tfoot-row">
                <td></td>
                <td></td>
                <td style={{ fontWeight: "bold" }}>Totais:</td>

                {/* TOTAL */}
                <td style={{ textAlign: "right", fontWeight: "bold" }}>
                  {formatNumeric(totalColunaTotal)}
                </td>

                {/* PAGO */}
                <td style={{ textAlign: "right", fontWeight: "bold" }}>
                  {formatNumeric(totalColunaPago)}
                </td>

                {/* VALOR A PAGAR */}
                <td style={{ textAlign: "right", fontWeight: "bold" }}>
                  {formatNumeric(totalColunaValorAPagar)}
                </td>
              </tr>
            </tfoot>

          </table>
        </div>

        {/* 🔹 Totais Iguais ao Fechar Comanda */}
        <div className="modal-footer">
          <div className="totais-box">
            {/* <div className="linha-total">
              <label>Total Comanda:</label>
              <span className="valor-total">{totalComanda}</span>
            </div>             */}

            <div className="linha-total">
              <label>Total a Pagar:</label>
              <input
                type="text"
                value={total}
                onChange={(e) => handleChangeTotal(e.target.value)}
                onFocus={handleFocusSelectAll}
                onBlur={handleBlurFormatTotal}
                inputMode="decimal"
                style={{ textAlign: "right" }}
              />
            </div>

            {totalRestante!=="0,00" && (
              <div className="linha-total">
                <label>Total Restante:</label>
                <span className="valor-total">{totalRestante}</span>
              </div>
            )}
            
          </div>

          <div className="modal-actions">
            <button className="btn-cancelar" onClick={onClose}>Cancelar</button>
            <button className="btn-print" onClick={() => onPrint(mesa, valores)}>Imprimir</button>
            <button className="btn-confirmar" onClick={handleConfirmar}>Confirmar</button>
          </div>
        </div>
      </div>

      <ModalFinalizarVenda
        show={showFinalizarVenda}
        onHide={() => setShowFinalizarVenda(false)}
        nota={nota}
        setNota={setNota}
        permitirEditarItens={true}
        onConfirm={async () => {
          const comandasAtualizadas = await fetchComandas(mesa.id);
          await imprimirComprovantesQuitados(comandasAntesPagamento.current, comandasAtualizadas);

          setShowFinalizarVenda(false);
          onClose();

          const mesaTotalmenteFechada = comandasAtualizadas.every(c => c.status === StatusComanda.Fechada);

          if (mesaTotalmenteFechada) {
            if (onMesaFechada) {
              await onMesaFechada();
            } else {
              showAlert(`Mesa #${mesa.codigo} finalizada com sucesso!`, "success");
              navigate("/mesaPage");
            }
          } else {
            showAlert("Pagamento registrado com sucesso!", "success");
          }
        }}
      />
    </div>
    </ModalPortal>
  );
};

export default FecharMesaModal;
